import { useCallback, useEffect, useMemo, useState } from 'react'
import type { TFunction } from 'i18next'
import {
  CUSTOM_TYPES,
  type ContentPick,
  type ContentProblem,
  type CustomRecord,
  type CustomType,
  type Effect,
  type EffectVocabulary,
  type FormField,
} from '@rollcall/contracts'
import type { Api } from '../api.js'
import { ApiError, ContentRefused, ValidationRefused } from '../api.js'
import { useT } from './i18n-context.js'
import { SectionHead } from './hint.js'
import { contentName } from './labels.js'
import { EffectEditor, effectText } from './effect-editor.js'
import { FieldList, emptyBody, valueAt, withValue, type Body } from './content-form.js'

/**
 * Свои записи контента (D53).
 *
 * Экран мастера, а не игрока: запись меняет числа на листе, за который
 * отвечает стол. Игрок приносит мастеру описание, мастер заводит.
 *
 * Редактор пошаговый, как визард создания персонажа, и по той же
 * причине: заполнять два десятка полей одной простынёй — значит
 * не заполнить их вовсе. Правка JSON не предлагается ни на одном
 * шаге: человек, готовый писать JSON, напишет его и без нас,
 * а остальным это не редактор.
 *
 * Незаконченная запись до кампании не доходит: сохранение бывает
 * только с последнего шага, и до него доходит только то, что прошло
 * схему на сервере.
 */

const STEPS = ['type', 'base', 'fields', 'effects', 'preview'] as const

type Step = (typeof STEPS)[number]

function problemsText(t: ReturnType<typeof useT>, problems: readonly ContentProblem[]) {
  return problems.map((problem) =>
    problem.path === null
      ? problem.message
      : t('custom.problemAt', { path: problem.path, message: problem.message }),
  )
}

/** Список записей владельца: сколько листов ссылается и что с ними можно. */
/**
 * Почему запись не сохранилась.
 *
 * «Сервер недоступен» — только когда сервера действительно не видно.
 * Отказ по полю, отказ по значению и пятисотка — три разных ответа,
 * и сводить их к сети значит послать мастера чинить связь, пока
 * сервер называет незаполненный ключ. Ровно это и происходило:
 * пустой ключ показывался как «Сервер недоступен».
 */
function fieldLabel(t: TFunction, field: string): string {
  // Путь поля внутри тела записи («body.mechanics.damage») человеку
  // не показывается целиком: в форме он видел последний сегмент.
  const key = field.split('.').pop() ?? field
  const label = t(`custom.field.${key}`, { defaultValue: '' })
  return label === '' ? key : label
}

export function contentFailureText(t: TFunction, error: unknown): string {
  if (error instanceof ValidationRefused) {
    // Одно поле — одно упоминание: схема отдаёт по замечанию
    // на каждое нарушенное правило, и «key, key» читается как две
    // разные беды. Имя поля переводится, если перевод есть:
    // «key» человек в форме не видел.
    const fields = [
      ...new Set(
        error.problems.map((problem) =>
          problem.field === '' ? t('custom.record') : fieldLabel(t, problem.field),
        ),
      ),
    ].join(', ')
    return t('errors.badFields', { fields })
  }
  if (error instanceof ApiError) {
    // Сервера не видно — статуса нет вовсе; всё остальное значит,
    // что сервер ответил, и «сеть» тут ни при чём.
    if (error.status === null) return t('errors.network')
    if (error.status >= 500) return t('errors.serverFailed')
    return t('errors.rejected')
  }
  return t('errors.network')
}

export function ContentListScreen({
  api,
  onNew,
  onEdit,
}: {
  readonly api: Api
  readonly onNew: () => void
  readonly onEdit: (record: CustomRecord) => void
}) {
  const t = useT()
  const [records, setRecords] = useState<CustomRecord[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [problems, setProblems] = useState<readonly ContentProblem[]>([])

  const reload = useCallback(() => {
    void api
      .listContent(null)
      .then(setRecords)
      .catch(() => {
        setError(t('errors.network'))
      })
  }, [api, t])

  useEffect(reload, [reload])

  const remove = (record: CustomRecord, confirmed: number | null) => {
    setProblems([])
    void api
      .removeContent(record.id, confirmed)
      .then(reload)
      .catch((cause: unknown) => {
        if (cause instanceof ContentRefused) setProblems(cause.problems)
        else setError(contentFailureText(t, cause))
      })
  }

  return (
    <section className="app-screen app-content">
      <SectionHead title={t('custom.title')} hints={[t('custom.hint')]} />

      {error !== null && (
        <p className="app-problems" role="alert">
          {error}
        </p>
      )}
      {problems.length > 0 && (
        <div className="app-problems" role="alert">
          {problemsText(t, problems).map((text) => (
            <p key={text}>{text}</p>
          ))}
        </div>
      )}

      {/* «Назад» отсюда нет: она вела на главную, а на главную
          ведёт пункт меню, и вторая дорога к тому же экрану —
          лишняя кнопка на каждом открытии списка. */}
      <div className="app-actions">
        <button type="button" className="app-button" onClick={onNew}>
          {t('custom.new')}
        </button>
      </div>

      {records !== null && records.length === 0 && (
        <p className="app-value-note">{t('custom.empty')}</p>
      )}

      <ul className="app-list">
        {(records ?? []).map((record) => (
          <li key={record.id} className="app-list-item">
            <div>
              <p className="app-list-title">{record.name}</p>
              <p className="app-value-note">
                {t(`custom.type.${record.type}`)} ·{' '}
                {record.usedBy === 0
                  ? t('custom.unused')
                  : t('custom.usedBy', { count: record.usedBy })}
              </p>
            </div>
            <div className="app-actions">
              <button
                type="button"
                className="app-button app-button-quiet"
                onClick={() => {
                  onEdit(record)
                }}
              >
                {t('custom.edit')}
              </button>
              <button
                type="button"
                className="app-button app-button-quiet"
                onClick={() => {
                  // Число подтверждается ровно то, которое показано:
                  // между показом и нажатием мог появиться ещё один
                  // персонаж, и тогда сервер откажет снова (D53).
                  remove(record, record.usedBy === 0 ? null : record.usedBy)
                }}
              >
                {record.usedBy === 0
                  ? t('custom.remove')
                  : t('custom.removeUsed', { count: record.usedBy })}
              </button>
            </div>
          </li>
        ))}
      </ul>
    </section>
  )
}

export function ContentEditorScreen({
  api,
  recordId,
  seatId,
  onDone,
  onCancel,
}: {
  readonly api: Api
  /** Правка существующей записи; `null` — новая. */
  readonly recordId: string | null
  readonly seatId: string | null
  readonly onDone: () => void
  readonly onCancel: () => void
}) {
  const t = useT()
  // Запись читается по адресу, а не приезжает пропсом: ссылка на правку
  // должна открывать правку и после перезагрузки (D27).
  const [record, setRecord] = useState<CustomRecord | null>(null)
  const [step, setStep] = useState<Step>('type')
  const [type, setType] = useState<CustomType>('item')
  const [fields, setFields] = useState<readonly FormField[] | null>(null)
  const [vocabulary, setVocabulary] = useState<EffectVocabulary | null>(null)
  const [body, setBody] = useState<Body>({})
  const [key, setKey] = useState('')
  const [note, setNote] = useState('')
  const [picks, setPicks] = useState<ContentPick[] | null>(null)
  const [search, setSearch] = useState('')
  const [problems, setProblems] = useState<readonly ContentProblem[]>([])
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    if (recordId === null) return
    let cancelled = false
    void api
      .listContent(null)
      .then((records) => {
        const found = records.find((item) => item.id === recordId)
        if (cancelled || found === undefined) return
        setRecord(found)
        setType(found.type)
        setBody(found.body as Body)
        setKey(found.contentId.split(':').pop() ?? '')
        setStep('fields')
      })
      .catch(() => {
        if (!cancelled) setError(t('errors.network'))
      })
    return () => {
      cancelled = true
    }
  }, [api, recordId, t])

  // Описание формы и словарь эффектов приходят с сервера: считать их
  // здесь значило бы держать вторые схемы в браузере (D53).
  useEffect(() => {
    let cancelled = false
    void api
      .contentForm(type)
      .then((next) => {
        if (cancelled) return
        setFields(next)
        // Тело заводится по описанию формы только у новой записи:
        // у существующей оно пришло с сервера, и затирать его пустым
        // значит потерять работу на первом же перерисовывании.
        setBody((current) =>
          Object.keys(current).length === 0 ? emptyBody(next) : current,
        )
      })
      .catch(() => {
        if (!cancelled) setError(t('errors.network'))
      })
    return () => {
      cancelled = true
    }
  }, [api, type, t])

  useEffect(() => {
    let cancelled = false
    void api
      .contentVocabulary(seatId)
      .then((next) => {
        if (!cancelled) setVocabulary(next)
      })
      .catch(() => {
        if (!cancelled) setError(t('errors.network'))
      })
    return () => {
      cancelled = true
    }
  }, [api, seatId, t])

  const change = (path: string, value: unknown) => {
    setBody((current) => withValue(current, path, value))
  }

  const effectsField = useMemo(
    () => (fields ?? []).find((field) => field.kind === 'effects'),
    [fields],
  )
  const effects = useMemo(() => {
    const raw = effectsField === undefined ? [] : valueAt(body, effectsField.path)
    return Array.isArray(raw) ? (raw as Effect[]) : []
  }, [body, effectsField])

  const name = typeof body['name'] === 'string' ? body['name'] : ''

  const loadPicks = (next: CustomType) => {
    setPicks(null)
    void api
      .contentPicks(next, seatId)
      .then(setPicks)
      .catch(() => {
        setError(t('errors.network'))
      })
  }

  const copyFrom = (id: string) => {
    setBusy(true)
    void api
      .contentCopy(id)
      .then((copy) => {
        setBody(copy.body as Body)
        setStep('fields')
      })
      .catch(() => {
        setError(t('errors.network'))
      })
      .finally(() => {
        setBusy(false)
      })
  }

  const save = () => {
    setBusy(true)
    setProblems([])
    // Прошлая неудача убирается вместе с попыткой: иначе рядом
    // с новым разбором висит старая строка, и человек чинит
    // то, что уже починил.
    setError(null)
    void api
      .saveContent({
        id: record?.id ?? null,
        type,
        key: key.trim(),
        body,
        note: note.trim() === '' ? null : note.trim(),
        // Правка механики использованной записи подтверждается числом
        // затронутых листов (D53). Число берётся то, которое показано
        // рядом с кнопкой.
        confirmedUsedBy: record === null || record.usedBy === 0 ? null : record.usedBy,
      })
      .then(() => {
        onDone()
      })
      .catch((cause: unknown) => {
        if (cause instanceof ContentRefused) setProblems(cause.problems)
        else setError(contentFailureText(t, cause))
      })
      .finally(() => {
        setBusy(false)
      })
  }

  const stepIndex = STEPS.indexOf(step)
  // Образец называется по-русски, как и везде: имя записи приходит
  // английским, и раньше список печатал его как есть. Поиск ищет
  // и по русскому, и по английскому — человек со справочником в руках
  // наберёт любое из двух.
  const needle = search.trim().toLocaleLowerCase('ru')
  const shownName = (pick: ContentPick) =>
    pick.custom ? pick.name : contentName(t, pick.id, pick.name)
  const filtered = (picks ?? []).filter(
    (pick) =>
      shownName(pick).toLocaleLowerCase('ru').includes(needle) ||
      pick.name.toLocaleLowerCase('ru').includes(needle),
  )

  return (
    <section className="app-screen app-content-editor">
      <SectionHead title={t('custom.editorTitle')} hints={[t('custom.editorHint')]} />
      <p className="app-value-note" data-testid="content-step">
        {t('custom.step', { step: stepIndex + 1, total: STEPS.length })} ·{' '}
        {t(`custom.steps.${step}`)}
      </p>

      {error !== null && (
        <p className="app-problems" role="alert">
          {error}
        </p>
      )}
      {problems.length > 0 && (
        <div className="app-problems" role="alert" data-testid="content-problems">
          {problemsText(t, problems).map((text) => (
            <p key={text}>{text}</p>
          ))}
        </div>
      )}

      {step === 'type' && (
        <div className="app-choice-list">
          {CUSTOM_TYPES.map((option) => (
            <button
              key={option}
              type="button"
              className={option === type ? 'app-button' : 'app-button app-button-quiet'}
              onClick={() => {
                setType(option)
                setBody({})
                setStep('base')
                loadPicks(option)
              }}
            >
              {t(`custom.type.${option}`)}
            </button>
          ))}
        </div>
      )}

      {step === 'base' && (
        <div>
          <div className="app-actions">
            <button
              type="button"
              className="app-button"
              onClick={() => {
                setBody(fields === null ? {} : emptyBody(fields))
                setStep('fields')
              }}
            >
              {t('custom.fromScratch')}
            </button>
          </div>
          <p className="app-field-hint">{t('custom.copyHint')}</p>
          <label className="app-field">
            <span className="app-field-label">{t('custom.searchLabel')}</span>
            <input
              value={search}
              onChange={(event) => {
                setSearch(event.target.value)
              }}
            />
          </label>
          <ul className="app-list">
            {filtered.slice(0, 40).map((pick) => (
              <li key={pick.id} className="app-list-item">
                <span data-testid="content-pick-name">
                  {shownName(pick)}
                  {pick.custom && ` · ${t('custom.own')}`}
                </span>
                <button
                  type="button"
                  className="app-button app-button-quiet"
                  disabled={busy}
                  onClick={() => {
                    copyFrom(pick.id)
                  }}
                >
                  {t('custom.copy')}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {step === 'fields' && fields !== null && (
        <form className="app-form" onSubmit={(event) => event.preventDefault()}>
          {record === null && (
            <label className="app-field">
              <span className="app-field-label">{t('custom.key')}</span>
              <input
                value={key}
                onChange={(event) => {
                  setKey(event.target.value)
                }}
              />
              <span className="app-field-hint">{t('custom.keyHint')}</span>
            </label>
          )}
          <label className="app-field">
            <span className="app-field-label">{t('custom.note')}</span>
            <input
              value={note}
              onChange={(event) => {
                setNote(event.target.value)
              }}
            />
          </label>
          <FieldList
            fields={fields}
            body={body}
            vocabulary={vocabulary}
            onChange={change}
            skipEffects
          />
        </form>
      )}

      {step === 'effects' && (
        <div>
          {effectsField === undefined || vocabulary === null ? (
            <p className="app-value-note">{t('custom.noEffects')}</p>
          ) : (
            <EffectEditor
              effects={effects}
              vocabulary={vocabulary}
              onChange={(next) => {
                change(effectsField.path, [...next])
              }}
            />
          )}
        </div>
      )}

      {step === 'preview' && (
        <div className="app-content-preview" data-testid="content-preview">
          <h3>{name === '' ? t('custom.noName') : name}</h3>
          <p className="app-value-note">{t(`custom.type.${type}`)}</p>
          <p>{typeof body['text'] === 'string' ? body['text'] : ''}</p>
          <p className="app-field-label">{t('custom.effectsPreview')}</p>
          {effects.length === 0 ? (
            <p className="app-value-note">{t('custom.noEffects')}</p>
          ) : (
            <ul>
              {effects.map((effect, index) => (
                <li key={`${effect.target}-${index}`}>{effectText(t, effect)}</li>
              ))}
            </ul>
          )}
          {record !== null && record.usedBy > 0 && (
            <p className="app-problems" data-testid="content-used-warning">
              {t('custom.usedWarning', { count: record.usedBy })}
            </p>
          )}
        </div>
      )}

      <div className="app-actions">
        {stepIndex > 0 && (
          <button
            type="button"
            className="app-button app-button-quiet"
            onClick={() => {
              // Назад — без потери набранного: тело живёт в состоянии
              // экрана, а шаги только показывают его части.
              setStep(STEPS[stepIndex - 1] ?? 'type')
            }}
          >
            {t('custom.back')}
          </button>
        )}
        {step !== 'preview' && step !== 'type' && (
          <button
            type="button"
            className="app-button"
            onClick={() => {
              setStep(STEPS[stepIndex + 1] ?? 'preview')
            }}
          >
            {t('custom.next')}
          </button>
        )}
        {step === 'preview' && (
          <button type="button" className="app-button" disabled={busy} onClick={save}>
            {t('custom.save')}
          </button>
        )}
        <button
          type="button"
          className="app-button app-button-quiet"
          onClick={onCancel}
        >
          {t('custom.cancel')}
        </button>
      </div>
    </section>
  )
}
